import { METRIC_CONFIG } from './config/metric.config';
import { metricRules } from './config/metric.rules';
import { MetricCard } from './MetricCard';
import { ChartResultProps } from './types/result-card.types';

export const ChartResult = ({
	resultData = [
		{ key: 'essential', score: 0 },
		{ key: 'optic', score: 0 },
		{ key: 'brain', score: 0 },
		{ key: 'skin', score: 0 },
		{ key: 'bone', score: 0 },
		{ key: 'immune', score: 0 },
	],
}: ChartResultProps) => {
	const firstColumn = resultData.slice(0, Math.ceil(resultData.length / 2));
	const secondColumn = resultData.slice(Math.ceil(resultData.length / 2));
	const rowCount = Math.max(firstColumn.length, secondColumn.length);

	const renderMetric = ({ key, score, title, icon, blocked = false }: (typeof resultData)[number]) => {
			
			const isMissingScore = score === null || score === undefined || Number.isNaN(Number(score));
			let displayTitle = title || key;
			if (isMissingScore) {
				displayTitle = `${displayTitle} (No data)`;
			}
			let iconElement: React.ReactNode = null;

			// Check if we have valid icon URL
			const hasValidIcon = icon && icon !== 'EMPTY' && icon.startsWith('http');

			if (hasValidIcon) {
				// Use dynamic icon from backend (URL)
				iconElement = <img src={icon} alt={displayTitle} className="brightness-0 invert" style={{ width: 32, height: 32, objectFit: 'contain' }} />;
			} else {
				// Try fallback to METRIC_CONFIG
				const typedKey = key as keyof typeof METRIC_CONFIG;
				const config = METRIC_CONFIG[typedKey];
				if (config) {
					if (!displayTitle) displayTitle = config.title;
					const Icon = config.icon;
					iconElement = <Icon />;
				} else {
					// Ultimate fallback: placeholder icon
					console.warn('⚠️ No icon available for:', key, '- Using placeholder');
					iconElement = (
						<div style={{ 
							width: 24, 
							height: 24, 
							background: '#E0E0E0', 
							borderRadius: '50%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							fontSize: '10px',
							color: '#666'
						}}>
							?
						</div>
					);
				}
			}
			
			// blocked metrics or score = 0 are outside the valid 1.0–3.0 range → cannot be dispensed
			const isNotApplicable = blocked || (!isMissingScore && Number(score) === 0);

			const scoreForMetric = isMissingScore ? 0 : Number(score);
			const barWidth = isMissingScore || isNotApplicable ? 0 : metricRules.barWidth(scoreForMetric);
			const barColor = isMissingScore || isNotApplicable ? '#BDBDBD' : metricRules.barColor(scoreForMetric);


			return (
				<MetricCard
					key={key}
					title={displayTitle}
					icon={iconElement}
					barWidth={barWidth}
					barColor={barColor}
					isNotApplicable={isNotApplicable}
				/>
			);
	};

	return (
		<div
			className="w-full mx-auto rounded-[10px] p-2 flex flex-col gap-2"
			style={{
				background: 'rgba(146, 200, 183, 0.19)',
			}}
		>
			{Array.from({ length: rowCount }).map((_, index) => {
				const left = firstColumn[index];
				const right = secondColumn[index];

				return (
					<div key={index} className="flex flex-col sm:flex-row gap-2 items-stretch">
						<div className="flex-1">{left ? renderMetric(left) : null}</div>
						<div className="flex-1">{right ? renderMetric(right) : null}</div>
					</div>
				);
			})}
		</div>
	);
};
